/// <amd-module name="Wiki/Editor/WikiEditorActionCreator" />
import { ICancelable, TimerManagement } from "VSS/Core/TimerManagement";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { traceError } from "VSS/Platform/Trace";

import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { WikiPage, WikiPageResponse, WikiType } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { getMarkdownText } from "Wiki/Editor/AttachmentHelpers";
import { IEditorActionCreator, IWikiEditorState } from "Wiki/Editor/Contracts";
import { showCloseDialog, showRenamePageDialog, showSaveDialog } from "Wiki/Editor/EditorDialogsHandler";
import { getPageTitleValidationError } from "Wiki/Editor/ErrorHelper";
import { FileSource } from "Wiki/Editor/FileSource";
import { getPageTextSize, isDirty, isInsertTextSupported, isRename } from "Wiki/Editor/Helpers";
import { PageSaveSource } from "Wiki/Editor/PageSaveSource";
import * as Resources from "Wiki/Editor/Resources";
import { EditorActions } from "Wiki/Editor/WikiEditorStore";
import { Attachment, GuidSuffixedFile, OperationStatus, WikiInfo, WikiPageInfoWithName } from "Wiki/ViewCommon/Contracts";
import { Areas, PageErrorType, TelemetryConstants } from "Wiki/ViewCommon/CustomerIntelligenceConstants";
import { getWikiTypeString, publish } from "Wiki/ViewCommon/Utils/WikiHelper";

const THROTTLE_TIME_IN_MS = 250;
const PAGE_SAVED_TOAST_DURATION_IN_MS = 3000;

// Maximum page content size allowed per file is 18MB. Following value is in bytes.
const MAX_PAGE_CONTENT_SIZE_IN_BYTES = 18 * 1024 * 1024;

export class EditorActionCreator implements IEditorActionCreator {
    private _timerManagement: TimerManagement;
    private _throttledUpdatePreviewContent: (content: string) => void;

    constructor(
        private _closeEditor: () => void,
        private _actions: EditorActions,
        private _getState: () => IWikiEditorState,
        private _pageContext: IVssPageContext,
        private _wikiType: WikiType,
        private _pageSaveCallback: (page: WikiPage, pageVersion: string, showRenameDialog?: boolean) => void,
        timerManagement?: TimerManagement // for UTs
    ) {
        this._timerManagement = timerManagement || new TimerManagement();
        this._throttledUpdatePreviewContent = this._timerManagement.throttle(this._handleTextChangeWithThrottling, THROTTLE_TIME_IN_MS);
    }

    public updatePageContent = (content: string, selectionStart: number, selectionEnd: number): void => {
        this._actions.pageContentUpdated.invoke({
            content,
            selectionStart,
            selectionEnd
        });
        this._throttledUpdatePreviewContent(content);
    };

    public updatePageSelection = (selectionStart: number, selectionEnd: number): void => {
        this._actions.pageSelectionUpdated.invoke({
            selectionStart,
            selectionEnd
        });
    };

    public updatePageName = (pageName: string, parentPath: string): void => {
        const pageNameError = getPageTitleValidationError(parentPath, pageName);
        this._actions.pageNameUpdated.invoke({ pageName, pageNameError });
    };

    public handleEditContentError = (errorMessage: string, errorType: PageErrorType): void => {
        traceError(this._pageContext, Areas.Wiki, TelemetryConstants.PageError, errorMessage, {
            errorType: errorType,
            wikiType: getWikiTypeString(this._wikiType)
        });

        this._actions.errorMessageUpdated.invoke(errorMessage);
    };

    public updateErrorMessage = (errorMessage?: string): void => {
        this._actions.errorMessageUpdated.invoke(errorMessage);
    };

    public savePage = (
        wikiInfo: WikiInfo,
        pageInfo: WikiPageInfoWithName,
        withMessage: boolean,
        isHandleBrokenLinksEnabled: boolean,
        closePageAfterSave: boolean = false
    ): void => {
        if (!isDirty(pageInfo, this._getState())) {
            if (closePageAfterSave) {
                this._closeEditor();
            }
            return;
        }

        if (this._handlePageSizeErrorIfLimitExceeds(this._getState().pageContent || "")) {
            return;
        }

        if (this._getState().savePageStatus === OperationStatus.InProgress) {
            return;
        }

        const pageName = this._getState().pageName;
        const errorMessage = getPageTitleValidationError(pageInfo.parentPath, pageName);

        if (!!errorMessage) {
            this._actions.pageNameErrorUpdated.invoke(errorMessage);
            return;
        }

        const isRenamed = isRename(pageInfo, this._getState());
        const isContentChanged = this._getState().pageContent !== pageInfo.content;
        const initialPageName = pageInfo.pageName;
        const defaultCommitMessage = _getDefaultCommitMessage(isRenamed, isContentChanged, initialPageName, pageName);

        this._actions.pageSaveStarted.invoke(closePageAfterSave);

        if (!withMessage && !isRenamed) {
            this._savePage(wikiInfo, pageInfo, defaultCommitMessage);
        } else {
            if (withMessage && isRenamed) {
                showSaveDialog(
                    this._pageContext,
                    !isHandleBrokenLinksEnabled,
                    true,
                    (customMessage?: string) => {
                        if (!isHandleBrokenLinksEnabled) {
                            this._savePage(wikiInfo, pageInfo, customMessage || defaultCommitMessage);
                        } else {
                            showRenamePageDialog(
                                this._pageContext,
                                () => {
                                    this._savePage(wikiInfo, pageInfo, customMessage || defaultCommitMessage);
                                },
                                this._onDismissRenameDialog,
                                wikiInfo,
                                pageInfo,
                                this._getState(),
                                customMessage || defaultCommitMessage
                            );
                        }
                    },
                    this._onDismissSaveDialog,
                    defaultCommitMessage
                );
            } else if (withMessage) {
                showSaveDialog(
                    this._pageContext,
                    false,
                    true,
                    (commitMessage: string) => {
                        this._savePage(wikiInfo, pageInfo, commitMessage);
                    },
                    this._onDismissSaveDialog,
                    defaultCommitMessage
                );
            } else if (!isHandleBrokenLinksEnabled) {
                showSaveDialog(
                    this._pageContext,
                    true,
                    false,
                    (commitMessage: string) => {
                        this._savePage(wikiInfo, pageInfo, commitMessage);
                    },
                    this._onDismissSaveDialog,
                    defaultCommitMessage
                );
            } else {
                showRenamePageDialog(
                    this._pageContext,
                    () => {
                        this._savePage(wikiInfo, pageInfo, defaultCommitMessage);
                    },
                    this._onDismissRenameDialog,
                    wikiInfo,
                    pageInfo,
                    this._getState(),
                    defaultCommitMessage
                );
            }
        }
    };

    public closeEditor = (pageInfo: WikiPageInfoWithName): void => {
        this.warnBeforePageSwitch(this._closeEditor, pageInfo);
    };

    public warnBeforePageSwitch(onLeavePage: () => void, pageInfo: WikiPageInfoWithName): void {
        if (isDirty(pageInfo, this._getState())) {
            publish(this._pageContext, TelemetryConstants.PageEditingCloseDialogPrompted, this._wikiType);
            showCloseDialog(this._pageContext, () => {
                publish(this._pageContext, TelemetryConstants.PageEditingAbandoned, this._wikiType);
                onLeavePage();
            });
        } else {
            onLeavePage();
        }
    }

    /**
     * Introduces text programatically in the text area. Invoking this method can trigger the handleChangeEvent again.
     * It is required to support redo/undo functionaly for programatically introduced text.
     */
    public insertTextProgramatically = (
        insertionText: string,
        selectionStart: number,
        selectionEnd: number,
        requirePreSpace: boolean = false
    ): void => {
        const content = this._getState().pageContent || "";
        if (requirePreSpace && selectionStart > 0 && !/\s/.test(content[selectionStart - 1])) {
            insertionText = " " + insertionText;
        }

        if (isInsertTextSupported(this._getState().pageContent)) {
            // Workaround for MS Edge issue, insertText doesn't work if text element contains empty string.
            document.execCommand("insertText", false, insertionText);
        } else {
            const currentText = content;
            const firstPart = currentText.substring(0, selectionStart);
            const endPart = currentText.substring(selectionEnd);
            const newCursorPosition = firstPart.length + insertionText.length;
            const newContent = firstPart + insertionText + endPart;
            this._actions.pageContentUpdated.invoke({
                content: newContent,
                selectionStart: newCursorPosition,
                selectionEnd: newCursorPosition
            });
            this._throttledUpdatePreviewContent(newContent);
        }
    };

    // added optional fileSource parameter and returning promise for UTs
    public addAttachments = (files: GuidSuffixedFile[], selectionStart: number, selectionEnd: number, fileSource?: FileSource): Promise<void> => {
        const _fileSource = fileSource || new FileSource();
        const attachments: Attachment[] = files.map(FileSource.createAttachment);
        const markdownText = getMarkdownText(files);
        this.insertTextProgramatically(markdownText, selectionStart, selectionEnd);
        publish(this._pageContext, TelemetryConstants.AttachmentsAdded, this._wikiType);
        this._actions.attachmentsAdded.invoke({ attachments });

        const promises: Promise<void>[] = [];
        for (const attachment of attachments) {
            promises.push(
                _fileSource.readContent(attachment).then((updatedAttachment: Attachment) => {
                    this._actions.attachmentRead.invoke(updatedAttachment);
                })
            );
        }

        return Promise.all(promises).then();
    };

    public togglePasteAsHtmlFlag = (): void => {
        this._actions.pasteAsHtmlToggled.invoke(undefined);
    };

    // public to simplify UTs
    public _handlePageSizeErrorIfLimitExceeds = (content: string): boolean => {
        const pageSize = getPageTextSize(content);
        if (pageSize > MAX_PAGE_CONTENT_SIZE_IN_BYTES) {
            const pageSizeInMB = Math.round((pageSize / (1024 * 1024)) * 100) / 100;
            const errorMessage: string = format(Resources.PageContentSizeExceeded, pageSizeInMB, MAX_PAGE_CONTENT_SIZE_IN_BYTES / (1024 * 1024));
            this.handleEditContentError(errorMessage, PageErrorType.PageSizeExceeded);
            return true;
        }

        return false;
    };

    // public, returning promise and optional PageSaveSource to simplify UTs
    public _savePage = (
        wikiInfo: WikiInfo,
        pageInfo: WikiPageInfoWithName,
        commitMessage: string,
        pageSaveSource?: PageSaveSource
    ): Promise<void> => {
        const _pageSaveSource = pageSaveSource || new PageSaveSource(this._pageContext);

        return _pageSaveSource
            .savePage(wikiInfo, pageInfo, this._getState(), commitMessage)
            .then(
                (response) => {
                    if (response) {
                        this._pageSaveCallback(response.page, response.eTag[0]);

                        const messageService = this._pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
                        messageService.addToast({
                            message: format(Resources.SavedPageMessage, commitMessage),
                            duration: PAGE_SAVED_TOAST_DURATION_IN_MS
                        });
                    }
                },
                (error: Error) => {
                    if (error && error.message && error.message.indexOf("TF402455") !== -1) {
                        this._actions.pageSaveFailed.invoke({
                            message: Resources.ErrorMessage_PullRequestRequired,
                            buttonProps: [
                                {
                                    text: Resources.ErrorMessage_LearnMoreText,
                                    href: Resources.ErrorMessage_LearnMoreUrl,
                                    target: "_blank"
                                }
                            ]
                        });
                    } else {
                        this._actions.pageSaveFailed.invoke({ message: error.message });
                    }
                }
            )
            .then(() => {
                if (_pageSaveSource.savedAttachments) {
                    this._actions.clearAttachments.invoke(_pageSaveSource.savedAttachments);
                }
            });
    };

    private _onDismissRenameDialog = (response?: WikiPageResponse, savedAttachments?: string[]): void => {
        if (savedAttachments) {
            this._actions.clearAttachments.invoke(savedAttachments);
        }
        if (response && response.eTag) {
            this._pageSaveCallback(response.page, response.eTag[0]);
        } else {
            this._actions.updateSavePageStatus.invoke(OperationStatus.NotStarted);
        }
    };

    private _onDismissSaveDialog = (): void => {
        this._actions.updateSavePageStatus.invoke(OperationStatus.NotStarted);
    };

    private _handleTextChangeWithThrottling = (content: string): void => {
        if (!this._handlePageSizeErrorIfLimitExceeds(content)) {
            this._actions.previewContentUpdated.invoke(content);
        }
    };
}

function _getDefaultCommitMessage(pageNameChanged: boolean, pageContentChanged: boolean, oldPageName: string, pageName: string): string {
    if (!oldPageName) {
        return format(Resources.AddPageDefaultComment, pageName);
    }
    if (pageNameChanged && pageContentChanged) {
        return format(Resources.EditAndRenamePageDefaultComment, oldPageName, pageName);
    } else if (pageNameChanged) {
        return format(Resources.RenamePageDefaultComment, oldPageName, pageName);
    } else {
        return format(Resources.EditPageDefaultComment, pageName);
    }
}
